/**
 * Embedded results view.
 *
 * Rendered inside the widget's full-screen overlay iframe. Deliberately reuses
 * the same search controller and result components as the standalone page — the
 * only differences are the host-driven theme and the replacement of the app
 * chrome with a configurable header.
 */

import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import CssBaseline from '@mui/material/CssBaseline';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeProvider } from '@mui/material/styles';
import { SearchBar, SearchFilterChips, SearchResults, StatusMessage } from '../components';
import { LANGUAGE_STORAGE_KEY } from '../components/LanguageSelector';
import { requestSearchBarFocus } from '../components/search/searchBarEvents';
import { useEmbedBridge, type EmbedSettings } from '../embed/useEmbedBridge';
import { useSearchController } from '../hooks/useSearchController';
import { createEmbedTheme } from '../embedTheme';
import { toResultSummary } from '../embedProtocol';
import { HISTORY_STORAGE_KEY, saveHistory } from '../searchCore/history';
import i18n from '../i18n';

const URL_FILTER_PARAMS = ['source', 'source_type', 'zenon_version', 'language'] as const;

export function EmbedPage() {
  const { t } = useTranslation('search');
  const bridge = useEmbedBridge();
  const [searchParams] = useSearchParams();
  const [booted, setBooted] = useState(false);

  const {
    query,
    setQuery,
    setFilters,
    filters,
    filtersRef,
    results,
    isLoading,
    error,
    page,
    totalPages,
    phraseHint,
    search,
    applyFilters,
    goToPage,
  } = useSearchController();

  // Direct visits (no widget) still work: everything falls back to URL params.
  const settings = useMemo<EmbedSettings>(() => {
    if (bridge.settings) return bridge.settings;

    const urlFilters: Record<string, string[]> = {};
    for (const param of URL_FILTER_PARAMS) {
      const values = searchParams.getAll(param);
      if (values.length > 0) urlFilters[param] = values;
    }

    return {
      query: searchParams.get('q') ?? '',
      filters: urlFilters,
      tokens: {},
      theme: searchParams.get('theme') === 'dark' ? 'dark' : 'light',
      uiLanguage: searchParams.get('lang') ?? undefined,
      history: [],
    };
  }, [bridge.settings, searchParams]);

  const waitingForHost = bridge.embedded && !bridge.ready;

  const theme = useMemo(
    () => createEmbedTheme(settings.tokens, settings.theme),
    [settings.tokens, settings.theme]
  );

  // Mirror the host's history so the overlay's own bar shows the same entries.
  useEffect(() => {
    if (waitingForHost || booted) return;

    if (settings.history.length > 0) {
      saveHistory(settings.history, HISTORY_STORAGE_KEY);
    }
    if (settings.uiLanguage) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, settings.uiLanguage);
      void i18n.changeLanguage(settings.uiLanguage);
    }

    setQuery(settings.query);
    setFilters(settings.filters);
    filtersRef.current = settings.filters;
    if (settings.query.trim()) search(settings.query);

    setBooted(true);
  }, [waitingForHost, booted, settings, setQuery, setFilters, filtersRef, search]);

  // The host bar was submitted again while the overlay was already open.
  useEffect(() => {
    if (!bridge.pushedQuery) return;
    setQuery(bridge.pushedQuery.value);
    search(bridge.pushedQuery.value);
  }, [bridge.pushedQuery, setQuery, search]);

  useEffect(() => {
    if (!settings.fontCssUrl) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = settings.fontCssUrl;
    document.head.appendChild(link);
    return () => link.remove();
  }, [settings.fontCssUrl]);

  const handleSearch = useCallback(
    (nextQuery: string) => {
      bridge.addHistory(nextQuery);
      search(nextQuery);
    },
    [bridge, search]
  );

  /** A query derived from the results (condition removed, fix, exact phrase). */
  const searchQuery = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      handleSearch(nextQuery);
    },
    [setQuery, handleSearch]
  );

  const editQuery = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      requestSearchBarFocus();
    },
    [setQuery]
  );

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      goToPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [goToPage]
  );

  // Result links must not open inside the overlay iframe; the host page decides
  // (via its result-click listener) or falls back to navigating its own tab.
  const handleResultsClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!bridge.embedded) return;
      const anchor = (event.target as HTMLElement).closest?.<HTMLAnchorElement>('a[href]');
      // `.href` is absolute, so in-app routes resolve against the search origin, not the host's.
      const href = anchor?.href;
      if (!anchor || !href) return;
      event.preventDefault();

      const resultId = anchor.closest<HTMLElement>('[data-result-id]')?.dataset.resultId;
      const index = resultId && results ? results.results.findIndex((r) => r.id === resultId) : -1;
      const result =
        index >= 0 && results
          ? toResultSummary({ ...results.results[index], position: results.from + index + 1 })
          : null;
      const newTab =
        event.ctrlKey || event.metaKey || event.shiftKey || anchor.target === '_blank';

      bridge.navigate(href, newTab, {
        query: results?.query ?? query,
        result: result ?? undefined,
      });
    },
    [bridge, results, query]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
        }}
      >
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            bgcolor: settings.tokens['header-bg'] ?? 'primary.main',
            color: settings.tokens['header-fg'] ?? 'primary.contrastText',
          }}
        >
          {settings.overlayLogoUrl && (
            <Box
              component="img"
              src={settings.overlayLogoUrl}
              alt=""
              sx={{ height: 20, width: 'auto' }}
            />
          )}
          <Typography variant="subtitle1" sx={{ flex: 1, color: 'inherit' }}>
            {settings.overlayTitle ?? t('headline')}
          </Typography>
          {bridge.embedded && (
            <IconButton
              onClick={bridge.close}
              size="small"
              sx={{ color: 'inherit' }}
              aria-label={t('close', { defaultValue: 'Close' })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Box sx={{ flex: 1, width: '100%', maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
          <Box sx={{ maxWidth: 800, mb: 3 }}>
            <SearchBar
              query={query}
              onQueryChange={setQuery}
              onSearch={handleSearch}
              isLoading={isLoading}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <SearchFilterChips filters={filters} onChange={applyFilters} />
          </Box>

          {error && <StatusMessage event="server-error" />}

          {!error && (
            <Box onClickCapture={handleResultsClick}>
              <SearchResults
                results={results}
                isLoading={isLoading}
                query={query}
                onSearchQuery={searchQuery}
                onEditQuery={editQuery}
                phraseHint={phraseHint}
              />
            </Box>
          )}

          {!error && results && totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
